import useResponsiveWidth from 'embed_v2/hooks/useResponsiveWidth'
import { WIDGET_TYPES, type Row as RowType, type WIDGET_TYPE } from 'embed_v2/types/version'
import { ComponentChildren } from 'preact'
import { memo } from 'preact/compat'
import { bgPositionToValue, widthToValue } from 'core/helpers/styles'

type Props = {
  id: RowType['id']
  title: RowType['title']
  options: RowType['options']
  children: ComponentChildren
  widgetType: WIDGET_TYPE
}

const Row = (props: Props) => {
  const { id, title, options } = props

  const width = widthToValue(options.width, options.width_fixed, options.width_percent)

  const responsiveStyles = useResponsiveWidth(width)

  const isRegistrationPage = props.widgetType === WIDGET_TYPES.REGISTRATION_PAGE

  const baseStyles = {
    boxSizing: 'border-box',
    display: 'flex',
    columnGap: `${options.column_gap ?? 0}px`,
    width,
    margin: '0 auto',
    backgroundRepeat: 'no-repeat',
    backgroundColor: `rgba(${options.bg_color})`,
    backgroundImage: options.bg_image_url ? `url(${options.bg_image_url})` : '',
    backgroundPosition:
      options.bg_image_position === 'advanced'
        ? options.bg_image_position_advanced
        : bgPositionToValue(options.bg_image_position),
    backgroundSize: options.bg_image_size === 'advanced' ? options.bg_image_size_advanced : options.bg_image_size,
    marginLeft: options.align === 'left' ? '0' : 'auto',
    marginRight: options.align === 'right' ? '0' : 'auto',
    marginTop: `${options.margin_top}px`,
    paddingTop: `${options.padding_top}px`,
    paddingRight: `${options.padding_right}px`,
    paddingBottom: `${options.padding_bottom}px`,
    paddingLeft: `${options.padding_left}px`,
    borderTop: options.border_side[0]
      ? `${options.border_size}px ${options.border_style} rgba(${options.border_color})`
      : 'none',
    borderRight: options.border_side[1]
      ? `${options.border_size}px ${options.border_style} rgba(${options.border_color})`
      : 'none',
    borderBottom: options.border_side[2]
      ? `${options.border_size}px ${options.border_style} rgba(${options.border_color})`
      : 'none',
    borderLeft: options.border_side[3]
      ? `${options.border_size}px ${options.border_style} rgba(${options.border_color})`
      : 'none',
    borderTopLeftRadius: `${options.border_top_left}px`,
    borderTopRightRadius: `${options.border_top_right}px`,
    borderBottomRightRadius: `${options.border_bottom_left}px`,
    borderBottomLeftRadius: `${options.border_bottom_right}px`,
    boxShadow: options.box_shadow
  }

  const styles = isRegistrationPage ? { ...baseStyles, ...responsiveStyles } : baseStyles

  return (
    <div id={`wf_row_${id}`} className={`wf_row wf_row_${id} wf_reset`} style={styles} data-title={title}>
      {props.children}
    </div>
  )
}

export default memo(Row, (prevProps, nextProps) => prevProps.id === nextProps.id)
